import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { fallbackPackages } from '../data/content';
import { useWebsiteContent } from '../WebsiteContentContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Check = () => (
  <svg className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);

const formatPrice = (aed) => `AED ${Number(aed || 0).toLocaleString()}`;

export const Packages = () => {
  const cms = useWebsiteContent();
  const [packages, setPackages] = useState(fallbackPackages);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly | annual
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/subscription-packages`, { timeout: 8000 });
        if (Array.isArray(res.data) && res.data.length > 0) {
          // Map API shape to UI shape; highlight middle plan; add cta label
          const mapped = res.data.map((p, i, arr) => ({
            id: p.id,
            name: p.name,
            monthly_price_aed: p.monthly_price_aed,
            annual_price_aed: p.annual_price_aed,
            staff_limit: p.staff_limit,
            features: p.features || [],
            cta: p.name && p.name.toLowerCase().includes('enterprise') ? 'Contact Sales' : 'Start Free Trial',
            highlighted: arr.length > 1 && i === Math.floor(arr.length / 2),
          }));
          setPackages(mapped);
        }
      } catch (e) {
        // keep fallback
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div data-testid="page-packages">
      {/* Hero */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600">{cms.packages_eyebrow}</span>
          <h1 className="mt-4 text-5xl sm:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">{cms.packages_title}</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">{cms.packages_subtitle}</p>

          {/* billing toggle */}
          <div className="mt-10 inline-flex items-center p-1.5 rounded-full bg-slate-200/70 border border-slate-200">
            <button
              type="button"
              data-testid="billing-monthly"
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition ${
                billingCycle === 'monthly' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              data-testid="billing-annual"
              onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition flex items-center gap-2 ${
                billingCycle === 'annual' ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Annual
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700">Save 17%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading && (
            <div data-testid="packages-loading" className="text-slate-500 mb-6">Loading latest pricing…</div>
          )}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${packages.length >= 3 ? 'lg:grid-cols-3' : ''} ${packages.length >= 4 ? 'xl:grid-cols-4' : ''} gap-6`}>
            {packages.map((p, i) => {
              const price = billingCycle === 'monthly' ? p.monthly_price_aed : Math.round((p.annual_price_aed || 0) / 12);
              const cycleSuffix = billingCycle === 'monthly' ? '/month' : '/month, billed annually';
              const isEnterprise = p.cta === 'Contact Sales';
              return (
                <div
                  key={p.id || i}
                  data-testid={`package-card-${i}`}
                  className={`relative p-8 rounded-3xl border transition flex flex-col ${
                    p.highlighted
                      ? 'border-blue-600 bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-2xl shadow-blue-600/30 scale-[1.02]'
                      : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-lg'
                  }`}
                >
                  {p.highlighted && (
                    <span className="absolute -top-3 right-6 px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-full bg-amber-400 text-amber-950">
                      Most popular
                    </span>
                  )}
                  <h3 className={`text-2xl font-bold ${p.highlighted ? 'text-white' : 'text-slate-900'}`}>
                    {p.name}
                  </h3>
                  <p className={`text-sm mt-2 ${p.highlighted ? 'text-blue-100' : 'text-slate-500'}`}>
                    {p.staff_limit >= 999999 ? 'Unlimited employees' : `Up to ${p.staff_limit} employee${p.staff_limit === 1 ? '' : 's'}`}
                  </p>
                  <div className="mt-6 flex items-baseline gap-1.5 whitespace-nowrap">
                    <span className={`text-5xl font-bold tracking-tight ${p.highlighted ? 'text-white' : 'text-slate-900'}`}>
                      {formatPrice(price)}
                    </span>
                  </div>
                  <p className={`mt-1 text-sm whitespace-nowrap ${p.highlighted ? 'text-blue-100' : 'text-slate-500'}`}>
                    {cycleSuffix}
                  </p>

                  <ul className="mt-8 space-y-3 flex-1">
                    {(p.features || []).map((f, fi) => (
                      <li key={fi} className="flex items-start gap-3 text-sm">
                        <span className={p.highlighted ? 'text-white' : ''}>
                          <Check />
                        </span>
                        <span className={p.highlighted ? 'text-blue-50' : 'text-slate-700'}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={isEnterprise ? '/contact' : '/register'}
                    data-testid={`package-cta-${i}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition ${
                      p.highlighted
                        ? 'bg-white text-blue-700 hover:bg-blue-50 shadow-lg'
                        : 'bg-slate-900 text-white hover:bg-blue-600'
                    }`}
                  >
                    {p.cta || 'Start Free Trial'}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ-ish strip */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl font-bold text-slate-900 mb-10">Common questions</h2>
          <div className="space-y-6">
            {(cms.packages_faq || []).map((f, i) => (
              <div key={i} data-testid={`faq-${i}`} className="p-6 rounded-2xl bg-white border border-slate-200">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
